import { FC } from 'react';
import { AllieFileInput, AllieProgressBar, AllieText } from '../../../components';
import {
  AssetsContainerStyled,
  AssetStyled,
  ToolbarAssetStyled,
  ButtonStyled,
  NameContainerStyled,
  AllieThumbnailStyled,
  AllieThumbnailUploadStyled,
} from './AssetsContainer.styles';
import {
  AllieDocumentOutlineIcon,
  AllieDownloadIcon,
  AllieTrashOutlineIcon,
  AllieUploadOutlineIcon,
} from '../../../styling/icons';

const fileTypesNoPreview = ['pdf', 'mp4'];

interface FileProps {
  id: number;
  fileName: string;
  extension?: string;
  signedUrl: string;
}

export interface AssetsContainerProps {
  files: FileProps[];
  onUpload: (files: any[]) => void;
  onRemove: (file: any) => void;
  labelUpload?: string;
  showProgressBar?: boolean;
  progress?: number;
  accepts: string[];
  maxFileSize: number;
  idFileUpload: string;
  onUploadError?: (error: any) => void;
  thumbnailWidth?: number;
  thumbnailShowName?: boolean;
  toolbarVisible?: boolean;
  showDownload?: boolean;
  fileUploadWidth?: number;
  fileUploadSize?: number;
}

export const AllieAssetsContainer: FC<AssetsContainerProps> = ({
  files,
  onUpload,
  onRemove,
  labelUpload,
  showProgressBar,
  progress = 0,
  idFileUpload,
  accepts,
  maxFileSize,
  onUploadError,
  thumbnailWidth = 152,
  thumbnailShowName = true,
  toolbarVisible = false,
  showDownload = true,
  fileUploadWidth = 152,
  fileUploadSize = 108,
}) => {
  const handleUpload = (file: any) => {
    onUpload([file]);
  };

  const handleFileDownload = async (file: FileProps) => {
    const fileUrl = file.signedUrl!;

    try {
      const response = await fetch(fileUrl);

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;

      link.download = file.fileName;

      link.click();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar el archivo:', error);
    }
  };

  const handleClickFile = (url: string) => {
    const fileUrl = url;
    window.open(fileUrl, '_blank');
  };

  const getExtension = (filename: string): string => {
    const ultimoPunto = filename.lastIndexOf('.');
    return ultimoPunto !== -1 ? filename.substring(ultimoPunto + 1) : '';
  };

  return (
    <AssetsContainerStyled>
      {files.map((file, index) => (
        <AssetStyled key={`${index}-${file.fileName}`} style={{ width: thumbnailWidth }}>
          <AllieThumbnailStyled
            borderRadius={4}
            size={65}
            onClick={() => handleClickFile(file.signedUrl)}
          >
            {fileTypesNoPreview.includes(file.extension || getExtension(file.fileName)) ? (
              <AllieDocumentOutlineIcon size="large" />
            ) : (
              <img src={file.signedUrl} alt={file.fileName} />
            )}
          </AllieThumbnailStyled>
          {thumbnailShowName && (
            <NameContainerStyled>
              <AllieText type="field3" color="neutral-90">
                {file.fileName}
              </AllieText>
            </NameContainerStyled>
          )}
          <ToolbarAssetStyled alwaysVisible={toolbarVisible}>
            {showDownload && (
              <ButtonStyled onClick={() => handleFileDownload(file)}>
                <AllieDownloadIcon size="small" allieColor='white' />
              </ButtonStyled>
            )}
            <ButtonStyled onClick={() => onRemove(file)}>
              <AllieTrashOutlineIcon size="large" />
            </ButtonStyled>
          </ToolbarAssetStyled>
        </AssetStyled>
      ))}
      <AllieFileInput
        id={idFileUpload}
        accept={accepts}
        maxFileSize={maxFileSize}
        onChange={handleUpload}
        onUploadError={onUploadError}
        render={(onClick) => (
          <AllieThumbnailUploadStyled borderRadius={4} size={fileUploadSize} width={fileUploadWidth} onclick={onClick}>
            <AllieUploadOutlineIcon size="large" />
            {labelUpload && (
              <AllieText type="field3" color="primary-60">
                {labelUpload}
              </AllieText>
            )}
            {showProgressBar && (
              <AllieProgressBar
                value={progress / 100}
                color="success"
                style={{ width: '100px', marginTop: '10px' }}
              />
            )}
          </AllieThumbnailUploadStyled>
        )}
      />
    </AssetsContainerStyled>
  );
};
